import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail, UserRound, LockKeyhole, Eye, EyeOff, MoreVertical, Bell, MessageCircle, LogOut, X, ChevronRight } from "lucide-react";
import accountApi from "@/lib/accountApi";
import HeroLanding from "@/components/pin-maker-hero/HeroLanding";


function PasswordInput({ className = "", ...props }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative block">
      <input
        {...props}
        type={visible ? "text" : "password"}
        className={`${className} pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Sembunyikan password" : "Lihat password"}
        title={visible ? "Sembunyikan password" : "Lihat password"}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </span>
  );
}

function AccountLoginForm({ initialMode = "login", onBack }) {
  const navigate = useNavigate();
  // PMS_SHARED_THEME_ACCOUNT_FORM_V1
  const [darkTheme, setDarkTheme] = useState(() => {
    try {
      const storedThemes = [
        localStorage.getItem("pms-theme"),
        localStorage.getItem("pinmaker-login-theme"),
        localStorage.getItem("pp_account_theme"),
      ];

      const savedTheme = storedThemes.find(
        value => value === "dark" || value === "light"
      ) || "dark";

      return savedTheme === "dark";
    } catch {
      return true;
    }
  });

  // Simpan satu pilihan tema untuk seluruh halaman PIN Maker Studio.
  useEffect(() => {
    const selectedTheme = darkTheme ? "dark" : "light";

    try {
      localStorage.setItem("pms-theme", selectedTheme);
      localStorage.setItem("pinmaker-login-theme", selectedTheme);
      localStorage.setItem("pp_account_theme", selectedTheme);
    } catch {
      // Tema aktif tetap berlaku selama komponen digunakan.
    }

    window.dispatchEvent(
      new CustomEvent("pms-theme-change", {
        detail: { theme: selectedTheme }
      })
    );
  }, [darkTheme]);
  // PMS_SHARED_THEME_SYNC_ACCOUNT_FORM_V2
  useEffect(() => {
    const syncTheme = (event) => {
      const next = event?.detail?.theme;
      if (next === "dark" || next === "light") {
        setDarkTheme(next === "dark");
      }
    };

    const syncFromStorage = () => {
      try {
        const next = localStorage.getItem("pms-theme");
        if (next === "dark" || next === "light") {
          setDarkTheme(next === "dark");
        }
      } catch {}
    };

    window.addEventListener("pms-theme-change", syncTheme);
    window.addEventListener("storage", syncFromStorage);

    return () => {
      window.removeEventListener("pms-theme-change", syncTheme);
      window.removeEventListener("storage", syncFromStorage);
    };
  }, []);

  const [mode, setMode] = useState(initialMode);
  // PASSWORD_RESET_OTP_V1
  const [resetStep, setResetStep] = useState("email");
  const [resetOtp, setResetOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [topMenuOpen, setTopMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    if (!notificationOpen) return undefined;
    const timer = setTimeout(() => setNotificationOpen(false), 3000);
    return () => clearTimeout(timer);
  }, [notificationOpen]);


  const isRegister = mode === "register";

  const submit = async (event) => {
    event.preventDefault();
    setError("");

    if (isRegister && password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }

    if (isRegister && password !== confirmPassword) {
      setError("Konfirmasi password tidak sama.");
      return;
    }

    setBusy(true);
    try {
      const endpoint = isRegister ? "/account/register" : "/account/login";
      const payload = isRegister
        ? { name: name.trim(), email: email.trim(), password }
        : { email: email.trim(), password };

      const response = await accountApi.post(endpoint, payload);
      const token = response.data?.token;

      if (!token) {
        throw new Error("Server tidak mengembalikan token akun.");
      }

      localStorage.setItem("pp_account_token", token);
      navigate("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(
        typeof detail === "string"
          ? detail
          : "Tidak dapat memproses akun. Periksa data dan coba lagi."
      );
    } finally {
      setBusy(false);
    }
  };

  const requestPasswordReset = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    if (!email.trim()) {
      setError("Masukkan alamat Gmail akun Anda.");
      return;
    }

    setBusy(true);
    try {
      const response = await accountApi.post(
        "/account/password-reset/request",
        { email: email.trim() }
      );
      setResetStep("otp");
      setResetOtp("");
      setNotice(
        response.data?.message ||
        "Jika email terdaftar, kode OTP akan dikirim."
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(typeof detail === "string" ? detail : "Gagal meminta kode OTP.");
    } finally {
      setBusy(false);
    }
  };

  const verifyPasswordResetOtp = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!/^\d{6}$/.test(resetOtp)) {
      setError("Masukkan kode OTP 6 digit.");
      return;
    }

    setBusy(true);
    try {
      const response = await accountApi.post(
        "/account/password-reset/verify",
        { email: email.trim(), otp: resetOtp }
      );
      if (!response.data?.reset_token) {
        throw new Error("Server tidak mengembalikan token verifikasi.");
      }
      setResetToken(response.data.reset_token);
      setResetStep("password");
      setNotice("OTP berhasil diverifikasi. Silakan buat password baru.");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(
        typeof detail === "string" ? detail : "OTP tidak valid atau kedaluwarsa."
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmPasswordReset = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");

    if (resetPassword.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    if (new TextEncoder().encode(resetPassword).length > 72) {
      setError("Password maksimal 72 byte.");
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setError("Konfirmasi password tidak sama.");
      return;
    }

    setBusy(true);
    try {
      const response = await accountApi.post(
        "/account/password-reset/confirm",
        { token: resetToken, password: resetPassword }
      );
      setNotice(
        response.data?.message ||
        "Password berhasil diubah. Silakan login kembali."
      );
      setResetToken("");
      setResetOtp("");
      setResetPassword("");
      setResetConfirmPassword("");
      setResetStep("email");
      setMode("login");
      window.history.replaceState({}, "", "/account");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      setError(
        typeof detail === "string" ? detail : "Gagal mengubah password."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-theme={darkTheme ? "dark" : "light"} className="pms-account-auth relative flex min-h-screen items-center justify-center px-4 py-10 transition-colors">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="absolute left-5 top-5 z-20 inline-flex items-center gap-2 rounded-full border border-slate-600/60 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-slate-100 shadow-lg backdrop-blur transition hover:bg-slate-800"
        >
          <span aria-hidden="true">←</span> Kembali ke beranda
        </button>
      )}
      
      <style>{`
        @keyframes pms-account-popover-in {
          from { opacity: 0; transform: translateY(-9px) scale(.96); filter: blur(2px); }
          to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes pms-account-modal-in {
          from { opacity: 0; transform: translateY(10px) scale(.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .pms-account-popover {
          transform-origin: top right;
          animation: pms-account-popover-in .26s cubic-bezier(.2,.8,.2,1) both;
        }
        .pms-account-modal-card {
          animation: pms-account-modal-in .22s ease-out both;
        }
        @media (prefers-reduced-motion: reduce) {
          .pms-account-popover, .pms-account-modal-card { animation: none !important; }
        }
      `}</style>

      <div className="absolute right-5 top-5 z-30">
        <button
          type="button"
          aria-label="Buka menu"
          title="Menu"
          aria-expanded={topMenuOpen}
          onClick={() => {
            setNotificationOpen(false);
            setTopMenuOpen(v => !v);
          }}
          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <MoreVertical className="h-5 w-5" />
        </button>

        {topMenuOpen && (
          <div className={`pms-account-popover absolute right-0 top-12 w-60 rounded-2xl border p-2 shadow-2xl ${
            darkTheme ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
          }`}>
            <button
              type="button"
              onClick={() => {
                setTopMenuOpen(false);
                setNotificationOpen(true);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                darkTheme ? "hover:bg-slate-800" : "hover:bg-blue-50"
              }`}
            >
              <Bell className="h-4 w-4 shrink-0 text-blue-400" />
              <span className="flex-1">Notifikasi</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setTopMenuOpen(false);
                setSupportOpen(true);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition-colors ${
                darkTheme ? "hover:bg-slate-800" : "hover:bg-blue-50"
              }`}
            >
              <MessageCircle className="h-4 w-4 shrink-0 text-emerald-400" />
              <span className="flex-1">Contact Support</span>
              <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />
            </button>

            <div className={`my-1 border-t ${darkTheme ? "border-slate-700" : "border-slate-200"}`} />

            <button
              type="button"
              onClick={() => {
                setTopMenuOpen(false);
                setLogoutConfirmOpen(true);
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold text-red-400 transition-colors hover:bg-red-500/10"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              <span>Keluar</span>
            </button>
          </div>
        )}

        {notificationOpen && (
          <div className={`pms-account-popover absolute right-0 top-12 w-72 rounded-2xl border p-4 shadow-2xl ${
            darkTheme ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
          }`}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-bold">Notifikasi</h3>
              <button
                type="button"
                aria-label="Tutup notifikasi"
                onClick={() => setNotificationOpen(false)}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-sm">Belum ada notifikasi.</p>
          </div>
        )}
      </div>

      {supportOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-support-title"
            className={`pms-account-modal-card w-full max-w-sm rounded-2xl border p-5 shadow-2xl ${
              darkTheme ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 id="account-support-title" className="text-lg font-bold">Contact / Support</h2>
                <p className="mt-1 text-sm text-slate-400">Hubungi developer jika membutuhkan bantuan.</p>
              </div>
              <button
                type="button"
                aria-label="Tutup support"
                onClick={() => setSupportOpen(false)}
                className="rounded-md p-1 hover:bg-slate-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <a
              href="https://wa.me/6281545901447"
              target="_blank"
              rel="noopener noreferrer"
              className={`mb-3 flex items-center gap-3 rounded-xl border p-3 ${
                darkTheme ? "border-slate-700 hover:bg-slate-800" : "border-slate-200 hover:bg-green-50"
              }`}
            >
              <MessageCircle className="h-6 w-6 shrink-0 text-green-500" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">WhatsApp Support</span>
                <span className="block text-sm text-green-500">0815-4590-1447</span>
                <span className="block text-xs text-slate-500">Klik untuk membuka WhatsApp</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0" />
            </a>

            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=fr.digitalprintingstudio@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-3 rounded-xl border p-3 ${
                darkTheme ? "border-slate-700 hover:bg-slate-800" : "border-slate-200 hover:bg-blue-50"
              }`}
            >
              <Mail className="h-6 w-6 shrink-0 text-blue-500" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Email Developer</span>
                <span className="block break-all text-sm text-blue-500">fr.digitalprintingstudio@gmail.com</span>
                <span className="block text-xs text-slate-500">Klik untuk mengirim email</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0" />
            </a>
          </section>
        </div>
      )}

      {logoutConfirmOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="account-logout-title"
            className={`pms-account-modal-card w-full max-w-sm rounded-2xl border p-5 shadow-2xl ${
              darkTheme ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
            }`}
          >
            <h2 id="account-logout-title" className="text-lg font-bold">Keluar dari halaman ini?</h2>
            <p className="mt-2 text-sm text-slate-400">Kamu akan kembali ke halaman beranda.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                className={`rounded-xl border px-4 py-2 text-sm font-semibold ${
                  darkTheme ? "border-slate-600 hover:bg-slate-800" : "border-slate-300 hover:bg-slate-100"
                }`}
              >
                Tidak, Batal
              </button>
              <button
                type="button"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  if (onBack) onBack();
                }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Ya, Keluar
              </button>
            </div>
          </section>
        </div>
      )}

      <section className={`w-full max-w-md rounded-2xl border p-6 shadow-xl transition-colors sm:p-8 ${
          darkTheme ? "border-slate-700 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-900"
        }`}>
        <div className="mb-7 text-center">
          <div className="mb-4 text-center text-3xl font-extrabold tracking-tight"><span>PIN Maker </span><span className="bg-gradient-to-r from-blue-400 via-sky-300 to-violet-400 bg-clip-text text-transparent">Studio</span></div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isRegister ? "Buat Akun" : "Login Akun"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isRegister
              ? "Daftar akun untuk melanjutkan ke Pin Maker."
              : "Masuk menggunakan Gmail Anda sebelum menggunakan Pin Maker."}
          </p>
        </div>

        {mode === "forgot" ? (
          <>
            <h1 className="mb-2 text-center text-2xl font-bold">
              {resetStep === "email"
                ? "Lupa Password"
                : resetStep === "otp"
                  ? "Verifikasi OTP"
                  : "Buat Password Baru"}
            </h1>
            <p className="mb-6 text-center text-sm text-muted-foreground">
              {resetStep === "email"
                ? "Masukkan alamat Gmail akun untuk menerima kode OTP."
                : resetStep === "otp"
                  ? "Masukkan kode 6 digit yang dikirim ke Gmail Anda. Kode berlaku 5 menit."
                  : "Buat password baru minimal 8 karakter."}
            </p>

            {resetStep === "email" ? (
              <form onSubmit={requestPasswordReset} className="space-y-4">
                <label className="block space-y-2">
                  <span className="text-sm font-medium">Email</span>
                  <input required type="email" autoComplete="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="nama@gmail.com"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm !text-gray-900 placeholder:text-gray-500 outline-none focus:ring-2 focus:ring-primary" />
                </label>
                {error && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
                {notice && <p role="status" className="rounded-lg bg-primary/10 p-3 text-sm">{notice}</p>}
                <button type="submit" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? "Mengirim..." : "Kirim Kode OTP"}
                </button>
              </form>
            ) : resetStep === "otp" ? (
              <form onSubmit={verifyPasswordResetOtp} className="space-y-4">
                <div className="rounded-lg bg-gray-100 p-3 text-sm !text-gray-900 dark:!text-gray-900">
                  Kode diminta untuk Gmail: <strong className="break-all !text-gray-900">{email}</strong>
                </div>
                <label className="block space-y-2">
                  <span className="text-sm font-medium">Kode OTP</span>
                  <input required type="text" inputMode="numeric" autoComplete="one-time-code"
                    pattern="[0-9]{6}" maxLength={6} value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="6 digit OTP"
                    className="w-full rounded-lg border border-input bg-background px-3 py-3 text-center text-lg tracking-[0.4em] outline-none focus:ring-2 focus:ring-primary" />
                </label>
                {error && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
                {notice && <p role="status" className="rounded-lg bg-primary/10 p-3 text-sm">{notice}</p>}
                <button type="submit" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? "Memverifikasi..." : "Verifikasi OTP"}
                </button>
                <button type="button" disabled={busy} onClick={requestPasswordReset}
                  className="w-full text-sm font-semibold text-primary hover:underline disabled:opacity-60">
                  Kirim ulang OTP
                </button>
              </form>
            ) : (
              <form onSubmit={confirmPasswordReset} className="space-y-4">
                <label className="block space-y-2">
                  <span className="text-sm font-medium">Password baru</span>
                  <PasswordInput required type="password" minLength={8} autoComplete="new-password"
                    value={resetPassword} onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Minimal 8 karakter"
                    className="w-full rounded-lg border border-input bg-background px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-medium">Konfirmasi password baru</span>
                  <PasswordInput required type="password" minLength={8} autoComplete="new-password"
                    value={resetConfirmPassword}
                    onChange={(e) => setResetConfirmPassword(e.target.value)}
                    placeholder="Ulangi password baru"
                    className="w-full rounded-lg border border-input bg-background px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-primary" />
                </label>
                {error && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
                {notice && <p role="status" className="rounded-lg bg-primary/10 p-3 text-sm">{notice}</p>}
                <button type="submit" disabled={busy}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground disabled:opacity-60">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {busy ? "Menyimpan..." : "Simpan Password Baru"}
                </button>
              </form>
            )}

            <button type="button" onClick={() => {
              setMode("login");
              setResetStep("email");
              setResetOtp("");
              setResetToken("");
              setResetPassword("");
              setResetConfirmPassword("");
              setError("");
              setNotice("");
              window.history.replaceState({}, "", "/account");
            }} className="mt-5 w-full text-sm font-semibold text-primary hover:underline">
              Kembali ke login
            </button>
          </>
        ) : (
          <>
        <form onSubmit={submit} className="space-y-4">
          {isRegister && (
            <label className="block space-y-2">
              <span className="text-sm font-medium">Nama</span>
              <span className="relative block">
                <UserRound className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <input
                  required
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nama Anda"
                  className="w-full rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
              </span>
            </label>
          )}

          <label className="block space-y-2">
            <span className="text-sm font-medium">Email</span>
            <span className="relative block">
              <Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nama@gmail.com"
                className="w-full rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </span>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-medium">Password</span>
            <span className="relative block">
              <LockKeyhole className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <PasswordInput
                required
                type="password"
                autoComplete={isRegister ? "new-password" : "current-password"}
                minLength={isRegister ? 8 : undefined}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? "Minimal 8 karakter" : "Password akun"}
                className="w-full rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </span>
          </label>

          {isRegister && (
            <label className="block space-y-2">
              <span className="text-sm font-medium">Konfirmasi Password</span>
              <span className="relative block">
                <LockKeyhole className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
                <PasswordInput
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ulangi password"
                  className="w-full rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
              </span>
            </label>
          )}

          {error && (
            <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? "Memproses..." : isRegister ? "Daftar Akun" : "Login"}
          </button>
        </form>

        {notice && <p role="status" className="mt-3 rounded-lg bg-primary/10 p-3 text-sm">{notice}</p>}

        {!isRegister && (
          <p className="mt-4 text-right text-sm">
            <button type="button" onClick={() => {
              setMode("forgot");
              setError("");
              setNotice("");
            }} className="font-semibold text-primary hover:underline">
              Lupa Password?
            </button>
          </p>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {isRegister ? "Sudah punya akun?" : "Belum punya akun?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(isRegister ? "login" : "register");
              setError("");
            }}
            className="font-semibold text-primary hover:underline"
          >
            {isRegister ? "Login di sini" : "Daftar sekarang"}
          </button>
        </p>
          </>
        )}
      </section>
        <footer className="pms-auth-site-footer">
          <p className="pms-auth-site-footer__main">
            © {new Date().getFullYear()} PIN Maker Studio. Semua hak dilindungi undang-undang.
          </p>
          
        </footer>
  </main>
  );
}

// PINMAKER_HERO_LANDING_WRAPPER_V1
export default function AccountLogin() {
  const [activeMode, setActiveMode] = useState(null);

  if (activeMode) {
    return (
      <AccountLoginForm
        key={activeMode}
        initialMode={activeMode}
        onBack={() => setActiveMode(null)}
      />
    );
  }

  const openLogin = () => setActiveMode("login");
  const openRegister = () => setActiveMode("register");

  return (
    <HeroLanding
      onLogin={openLogin}
      onRegister={openRegister}
      onStart={openRegister}
    />
  );
}
